import React, { useEffect, useState } from 'react';
import JSZip from 'jszip';
import config from 'config';
import {
  assetUrls,
  statusLabel,
  statusVariants,
  verificationStatusList,
} from 'containers/CreativePreview/constants';
import { CreativeContainerStyles } from './style';
import {
  useFetchAssetsListQuery,
  useFetchMetadataQuery,
} from 'services/api/creativePreview';
import { MdDescription } from 'react-icons/md';
import LogModal from '../LogModal';
import { Badge, Spinner } from 'react-bootstrap';
import { get, pick } from 'lodash';
import CreativeMetadataModal from '../CreativeMetadataModal';
import VerificationStatus from './VerificationStatus';
import PreviewModal from './PreviewModal';
import {
  FaDownload,
  FaExpandArrowsAlt,
  FaQuestionCircle,
} from 'react-icons/fa';
import Carousel from './Carousel';
import { Promise } from 'bluebird';
import Tooltip from 'reusable/Tooltip';
import { toast } from 'react-toastify';
import { fetchAsset } from './helpers';

interface urlItem {
  url: string;
  label?: string;
  previewUrl?: string;
  playbackRate?: number;
  placeholder?: string;
}

const helpText = {
  verificationStatus: (
    <div>
      <div>
        It represents if CTA was manually verified. Click status for more info.
      </div>
    </div>
  ),
  status: (
    <div>
      <div>
        It represents if test is completed/pending/running. Once completed, it
        represents if test was successful i.e. creative was clicked. Click
        status for more info.
      </div>
    </div>
  ),
  alphCrid: (
    <div>
      <div>
        It represents alph crid being tested. Click crid to get more info
        related to creative, such as adType, clickThrough, pixels etc.
      </div>
    </div>
  ),
};

function CreativeBlock({ data, creativeFetchTime }) {
  const [progressLogs, setProgressLogs] = useState('');
  const [showLogs, setShowLogs] = useState(false);
  const [showMetadata, setShowMetadata] = useState({ show: false, data: {} });
  const [showPreview, setShowPreview] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  const { data: creativeHeaders = [] } =
    useFetchMetadataQuery('creativeHeaders');

  const {
    data: assetData,
    isSuccess: isAssetsSuccess,
    isLoading: isAssetsLoading,
    refetch: refetchAssets,
  } = useFetchAssetsListQuery({ id: data._id });

  useEffect(() => {
    if (creativeFetchTime) {
      refetchAssets();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [creativeFetchTime]);

  const assetsList = get(assetData, 'assets', []);

  useEffect(() => {
    if (isAssetsSuccess) {
      const progressAsset = assetsList.find(
        asset => asset.fileName === 'progress.txt',
      );
      if (progressAsset) {
        const url = `${config.serverUrl}/api/creative-testing/v1/asset/${progressAsset.fileName}?path=${progressAsset.path}`;
        fetchAsset(url)
          .then(res => setProgressLogs(res.data))
          .catch(err => console.error(err));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAssetsSuccess]);

  const carouselItemUrls: urlItem[] = [];

  assetUrls.forEach(({ id, label }) => {
    if (get(data, `testMetadata.${id}`, ''))
      carouselItemUrls.push({
        url: get(data, `testMetadata.${id}`, ''),
        label,
      });
  });

  assetsList.forEach(asset => {
    const url = `${config.serverUrl}/api/creative-testing/v1/asset/${asset.fileName}?path=${asset.path}`;
    const previewUrl = `${window.location.origin}/asset-preview/${asset.fileName}?path=${asset.path}`;
    const placeholder = asset.placeholder
      ? `${config.serverUrl}/api/creative-testing/v1/asset/${asset.placeholder.fileName}?path=${asset.placeholder.path}`
      : '';

    if (/\.(jpg|jpeg|png|webp|avif|gif|svg|mp4|ogg|3gp)$/.test(asset.fileName))
      carouselItemUrls.push({
        url,
        previewUrl,
        label: asset.label,
        playbackRate: 3,
        placeholder,
      });
  });

  const downloadAssets = async (urls: string[]) => {
    setIsDownloading(true);
    const zip = new JSZip();

    try {
      await Promise.map(urls, async url => {
        const { data, localURL } = await fetchAsset(url);

        const fileName = url.split('?')[0].split('/').pop();
        if (fileName) zip.file(fileName, data);
        if (localURL) URL.revokeObjectURL(localURL);
      });

      const zipBlob = await zip.generateAsync({ type: 'blob' });
      const link = URL.createObjectURL(zipBlob);
      const fileName = `${get(data, 'testMetadata.alph_crid', '')}_${get(
        data,
        'timestamp',
      )}_${get(data, 'osv')}.zip`;

      const downloadLink = document.createElement('a');
      downloadLink.href = link;
      downloadLink.download = fileName;
      downloadLink.click();
      downloadLink.remove();
      window.setTimeout(() => URL.revokeObjectURL(link), 1000);
    } catch (e: any) {
      toast.error(`Error occurred while downloading assets : ${e.message}`);
      console.error(e);
    }

    setIsDownloading(false);
  };

  return (
    <CreativeContainerStyles>
      <div className="creative-block-header">
        <div className="creative-id">
          <div>
            Verification Status
            <Tooltip tooltipValue={helpText.verificationStatus}>
              <div>
                <FaQuestionCircle />
              </div>
            </Tooltip>
            :
            <Badge
              bg={get(
                verificationStatusList.find(
                  el => el.title === data.verificationStatus,
                ),
                'variant',
                'secondary',
              )}
              onClick={() =>
                setShowMetadata({
                  show: true,
                  data: pick(data, [
                    'verificationStatus',
                    'comment',
                    'verifiedBy',
                  ]),
                })
              }
              style={{ cursor: 'pointer' }}
            >
              {get(data, 'verificationStatus')}
            </Badge>
          </div>
          <div>
            Status
            <Tooltip tooltipValue={helpText.status}>
              <div>
                <FaQuestionCircle />
              </div>
            </Tooltip>
            :
            <Badge
              bg={statusVariants[data.status]}
              onClick={() =>
                setShowMetadata({
                  show: true,
                  data: get(data, 'creativeMetadata', {}),
                })
              }
              style={{ cursor: 'pointer' }}
            >
              {statusLabel[data.status]}
            </Badge>
          </div>
          <div>
            Alph CRID
            <Tooltip tooltipValue={helpText.alphCrid}>
              <div>
                <FaQuestionCircle />
              </div>
            </Tooltip>
            :
            <Badge
              bg={'primary'}
              onClick={() =>
                setShowMetadata({
                  show: true,
                  data: get(data, 'testMetadata', {}),
                })
              }
              style={{ cursor: 'pointer' }}
            >
              {get(data, 'testMetadata.crid')}
            </Badge>
          </div>
          {creativeHeaders.map(el => (
            <div key={el.title}>
              {el.title} : {get(data, el.path)}
            </div>
          ))}
          <Tooltip tooltipValue="Expand">
            <div>
              <FaExpandArrowsAlt
                onClick={() => setShowPreview(true)}
                className="icon"
              />
            </div>
          </Tooltip>
          <Tooltip tooltipValue="Logs">
            <div>
              <MdDescription
                className={progressLogs ? 'icon' : 'icon-disabled'}
                onClick={() => setShowLogs(true)}
              />
            </div>
          </Tooltip>
          <Tooltip tooltipValue="download assets">
            <div>
              <FaDownload
                className={isDownloading ? 'icon-disabled' : 'icon'}
                onClick={() =>
                  downloadAssets(carouselItemUrls.map(el => el.url))
                }
              />
            </div>
          </Tooltip>
        </div>
        {get(data, 'verificationStatus') === 'Not Checked' &&
          !['Pending', 'Running'].includes(get(data, 'status')) && (
            <VerificationStatus id={get(data, '_id')} />
          )}
      </div>

      {isAssetsLoading ? (
        <Spinner animation="border" />
      ) : (
        <Carousel urlList={carouselItemUrls} />
      )}
      <LogModal
        show={showLogs}
        onHide={() => setShowLogs(false)}
        logs={progressLogs || ''}
      />
      <CreativeMetadataModal
        show={showMetadata.show}
        onHide={() => setShowMetadata({ show: false, data: {} })}
        data={showMetadata.data}
      />
      <PreviewModal
        alphCrid={get(data, 'testMetadata.alph_crid', '')}
        adSlot={get(data, 'adSlot', '')}
        show={showPreview}
        urlList={carouselItemUrls}
        onHide={() => setShowPreview(false)}
      />
    </CreativeContainerStyles>
  );
}

export default CreativeBlock;
